import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { taskPhotoService } from '../../../services/taskPhotoService';

const PhotoCard = ({ photo, onClick, canManage, onToggleClientVisible }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!photo?.filePath) return;
      const { url, error: err } = await taskPhotoService?.getSignedUrl(photo?.filePath, 3600);
      if (cancelled) return;
      if (err) {
        console.error('[PhotoGrid] thumbnail signed URL failed:', { photoId: photo?.id, code: err?.code, message: err?.message });
        setImgError(true);
      }
      setImgUrl(url || null);
      setImgLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [photo?.filePath]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div
      className="group relative bg-card border border-border rounded-xl overflow-hidden cursor-pointer hover:shadow-elevation-2 transition-all hover:-translate-y-0.5"
      onClick={() => onClick?.(photo, imgUrl)}
    >
      {/* Image */}
      <div className="aspect-[4/3] bg-muted overflow-hidden">
        {imgLoading && (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-border border-t-accent rounded-full animate-spin" />
          </div>
        )}
        {imgError && (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <Icon name="ImageOff" size={24} />
            <span className="text-xs">Failed to load</span>
          </div>
        )}
        {imgUrl && !imgError && (
          <img
            src={imgUrl}
            alt={`Task photo for ${photo?.task?.title || 'task'}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Icon name="ZoomIn" size={18} className="text-foreground" />
          </div>
        </div>
      </div>
      {/* Meta */}
      <div className="p-3">
        <p className="text-xs font-semibold text-foreground truncate">{photo?.task?.title || 'Site Photo'}</p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-muted-foreground truncate">{photo?.uploader?.full_name || 'Unknown'}</span>
          <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{formatDate(photo?.createdAt)}</span>
        </div>
        {photo?.task?.category && (
          <span className="inline-block mt-1.5 px-2 py-0.5 bg-muted rounded-full text-xs text-muted-foreground">
            {photo?.task?.category}
          </span>
        )}
        {canManage ? (
          <button
            type="button"
            onClick={(e) => {
              e?.stopPropagation();
              onToggleClientVisible?.(photo?.id, !photo?.clientVisible);
            }}
            className={`mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              photo?.clientVisible
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                : 'bg-muted border-border text-muted-foreground hover:bg-muted/80'
            }`}
            title={photo?.clientVisible ? 'Visible to client — click to hide' : 'Hidden from client — click to show'}
          >
            <Icon name={photo?.clientVisible ? 'Eye' : 'EyeOff'} size={12} />
            {photo?.clientVisible ? 'Visible to client' : 'Hidden from client'}
          </button>
        ) : photo?.clientVisible === false ? (
          <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded-full text-xs text-muted-foreground">
            <Icon name="EyeOff" size={11} />
            Hidden from client
          </span>
        ) : null}
      </div>
    </div>
  );
};

const PhotoGrid = ({ photos, onPhotoClick, loading, canManage, onToggleClientVisible }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 })?.map((_, i) => (
          <div key={i} className="bg-muted rounded-xl overflow-hidden animate-pulse">
            <div className="aspect-[4/3] bg-muted/70" />
            <div className="p-3 space-y-2">
              <div className="h-3 bg-muted/70 rounded w-3/4" />
              <div className="h-3 bg-muted/70 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!photos?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mb-4">
          <Icon name="Images" size={28} className="text-muted-foreground" />
        </div>
        <h3 className="text-base font-semibold text-foreground">No photos yet</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-xs">Photos uploaded by workers when completing tasks will appear here.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {photos?.map(photo => (
        <PhotoCard
          key={photo?.id}
          photo={photo}
          onClick={onPhotoClick}
          canManage={canManage}
          onToggleClientVisible={onToggleClientVisible}
        />
      ))}
    </div>
  );
};

export default PhotoGrid;
